import React, { useState } from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormClearErrors,
  UseFormGetValues,
} from "react-hook-form";
import PhoneInput from "../../components/PhoneInput";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { MIN_PASSWORD_LENGTH, REGEXES } from "../../lib/constants";

interface BasicInfo {
  firstName: string;
  lastName: string;
  company: string;
  website: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

interface BasicInfoProps {
  register: UseFormRegister<BasicInfo>;
  errors: FieldErrors<BasicInfo>;
  setValue: any;
  clearErrors: UseFormClearErrors<BasicInfo>;
  setSelectedCode: (arg: string) => void;
  selectedCode: string;
  phoneNumber: string;
  onPhoneChange: (code: string, number: string) => void;
  getValues: UseFormGetValues<BasicInfo>;
}

const BasicInformation: React.FC<BasicInfoProps> = ({
  register,
  errors,
  setValue,
  clearErrors,
  setSelectedCode,
  selectedCode,
  phoneNumber,
  onPhoneChange,
  getValues,
}) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const handleInputChange = (fieldPath: string) => {
    clearErrors(fieldPath as keyof BasicInfo);
  };

  const handleCodeChange = (newCode: string) => {
    clearErrors("phone");
    setSelectedCode(newCode);
    onPhoneChange(newCode, phoneNumber);
  };

  const handlePhoneInputChange = (phone: string) => {
    clearErrors("phone");
    const value = phone === " " ? undefined : phone;
    setValue("phone", value);
    onPhoneChange(selectedCode, value as string);
    handleInputChange("phone");
  };

  return (
    <div className="xxl:space-y-7 space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="xxxl:text-2xl xxl:text-xl text-lg font-semibold text-black">
          Basic information
        </h2>
        <span className="text-sm font-normal mr-1 text-customGrayBase">
          Step 1/3
        </span>
      </div>
      <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
        <div className="col-span-1">
          <label className="block xxl:text-sm text-xs font-semibold text-customGrayDark">
            First Name<span className="text-red-500">*</span>
          </label>
          <input
            autoComplete="new-password"
            className={`mt-1 block w-full px-2 xxl:py-3 py-2 border ${
              errors?.firstName ? "border-red-500" : ""
            } border-gray-300 focus:border-customGreenColor focus:outline-customGreenColor rounded-lg`}
            {...register("firstName", {
              required: "This field is required",
            })}
            onChange={() => handleInputChange("firstName")}
          />
          {errors?.firstName && (
            <p className="text-red-600 text-sm">{errors?.firstName.message}</p>
          )}
        </div>
        <div className="col-span-1">
          <label className="block xxl:text-sm text-xs font-semibold text-customGrayDark">
            Last Name<span className="text-red-500">*</span>
          </label>
          <input
            autoComplete="new-password"
            className={`mt-1 block w-full  px-2 xxl:py-3 py-2 border ${
              errors?.lastName ? "border-red-500" : ""
            } border-gray-300 focus:border-customGreenColor focus:outline-customGreenColor rounded-lg`}
            {...register("lastName", {
              required: "This field is required",
            })}
            onChange={() => handleInputChange("lastName")}
          />
          {errors?.lastName && (
            <p className="text-red-600 text-sm">{errors?.lastName.message}</p>
          )}
        </div>
        <div className="col-span-1">
          <label className="block xxl:text-sm text-xs font-semibold text-customGrayDark">
            Company<span className="text-red-500">*</span>
          </label>
          <input
            autoComplete="new-password"
            className={`mt-1 block w-full px-2 xxl:py-3 py-2 border ${
              errors?.company ? "border-red-500" : ""
            } border-gray-300 focus:border-customGreenColor focus:outline-customGreenColor rounded-lg`}
            {...register("company", {
              required: "This field is required",
            })}
            onChange={() => handleInputChange("company")}
          />
          {errors?.company && (
            <p className="text-red-600 text-sm">{errors?.company.message}</p>
          )}
        </div>
        <div className="col-span-1">
          <label className="block xxl:text-sm text-xs font-semibold text-customGrayDark">
            Website
          </label>
          <input
            autoComplete="new-password"
            className={`mt-1 block w-full px-2 xxl:py-3 py-2 border ${
              errors?.website ? "border-red-500" : ""
            } border-gray-300 focus:border-customGreenColor focus:outline-customGreenColor rounded-lg`}
            {...register("website", {
              validate: (value) => {
                return (
                  value === undefined ||
                  value == "" ||
                  (REGEXES.URL.test(value)
                    ? true
                    : "Please enter a valid website url")
                );
              },
            })}
            onChange={() => handleInputChange("website")}
          />
          {errors?.website && (
            <p className="text-red-600 text-sm">{errors?.website.message}</p>
          )}
        </div>
        <div className="col-span-1">
          <label className="block xxl:text-sm text-xs font-semibold text-customGrayDark">
            Email<span className="text-red-500">*</span>
          </label>
          <input
            autoComplete="new-password"
            className={`mt-1 block w-full px-2 xxl:py-3 py-2 border ${
              errors?.email ? "border-red-500" : ""
            } border-gray-300 focus:border-customGreenColor focus:outline-customGreenColor rounded-lg`}
            {...register("email", {
              required: "This field is required",
              validate: (value) => {
                return (
                  value === undefined ||
                  value == "" ||
                  (REGEXES.EMAIL.test(value)
                    ? true
                    : "Please enter a valid email address")
                );
              },
            })}
            onChange={() => handleInputChange("email")}
          />
          {errors?.email && (
            <p className="text-red-600 text-sm">{errors?.email.message}</p>
          )}
        </div>
        <div className="col-span-1">
          <label className="block xxl:text-sm text-xs font-semibold text-customGrayDark  ">
            Phone<span className="text-red-500">*</span>
          </label>
          <PhoneInput
            selectedCode={selectedCode}
            error={errors?.phone ? true : false}
            selectedNumber={phoneNumber}
            setSelectedNumber={handlePhoneInputChange}
            setSelectedCode={handleCodeChange}
            {...register("phone", {
              required: "This field is required",
              validate: (value) => {
                if (value && value.trim() !== "") {
                  return REGEXES.PHONE.test(value)
                    ? true
                    : "Phone number must be exactly 10 digits";
                }
                return true;
              },
            })}
          />
          {errors?.phone && (
            <p className="text-red-600 text-sm">{errors?.phone.message}</p>
          )}
        </div>
        <div className="col-span-1">
          <label className="block xxl:text-sm text-xs font-semibold text-customGrayDark">
            Password<span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              autoComplete="new-password"
              type={showPassword ? "text" : "password"}
              className={`mt-1 block w-full px-2 xxl:py-3 py-2 border ${
                errors?.password ? "border-red-500" : ""
              } border-gray-300 focus:border-customGreenColor focus:outline-customGreenColor rounded-lg`}
              {...register("password", {
                required: "This field is required",
                validate: (value) => {
                  if (value.length < MIN_PASSWORD_LENGTH)
                    return "Password must be at least 8 characters";

                  return (
                    value === undefined ||
                    value == "" ||
                    (REGEXES.PASSWORD.test(value)
                      ? true
                      : "Password must include both upper and lower case characters")
                  );
                },
              })}
              id="password"
              onChange={() => handleInputChange("password")}
            />
            <div
              className="absolute inset-y-0 right-0 flex items-center px-3 cursor-pointer"
              style={{ top: "50%", transform: "translateY(-50%)" }}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <AiOutlineEyeInvisible className="xxl:text-2xl text-xl text-customGrayBase" />
              ) : (
                <AiOutlineEye className="xxl:text-2xl text-xl text-customGrayBase" />
              )}
            </div>
          </div>
          {errors?.password && (
            <p className="text-red-600 text-sm">{errors?.password.message}</p>
          )}
        </div>

        <div className="col-span-1">
          <label className="inline-block lg:block xxl:text-sm text-xs font-semibold text-customGrayDark">
            Confirm Password<span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              autoComplete="new-password"
              type={showConfirmPassword ? "text" : "password"}
              className={`mt-1 block w-full px-2 xxl:py-3 py-2 border ${
                errors?.confirmPassword ? "border-red-500" : ""
              } border-gray-300 focus:border-customGreenColor focus:outline-customGreenColor rounded-lg`}
              {...register("confirmPassword", {
                required: "This field is required",
                validate: (value) => {
                  return (
                    value === getValues("password") || "Passwords do not match"
                  );
                },
              })}
              id="confirmPassword"
              onChange={() => handleInputChange("confirmPassword")}
            />
            <div
              className="absolute inset-y-0 right-0 flex items-center px-3 cursor-pointer"
              style={{ top: "50%", transform: "translateY(-50%)" }}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <AiOutlineEyeInvisible className="xxl:text-2xl text-xl text-customGrayBase" />
              ) : (
                <AiOutlineEye className="xxl:text-2xl text-xl text-customGrayBase" />
              )}
            </div>
          </div>
          {errors?.confirmPassword && (
            <p className="text-red-600 text-sm">
              {errors?.confirmPassword.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BasicInformation;
